import { useMemo } from 'react';
import { NULL_VALUE_TITLE, useGetChartOptionsDataProps } from './constants';
import { GetGlobalExpData } from 'utils/Global';

export const useGetChartOptionsData = (props: useGetChartOptionsDataProps) => {
  const { selectedChart, binSize, isNumericChart } = props;
  const useBinning = isNumericChart && binSize > 1;
  return useMemo(() => {
    const selectedChartData = selectedChart?.data;
    let seriesData = [],
      categories = [],
      xValues = [],
      colorsMap = {};

    if (!selectedChartData || selectedChartData.length === 0) {
      return { seriesData, categories, xValues, colorsMap };
    }

    const keys = Object.keys(selectedChartData[0]);
    const nameKey = keys[0];
    const scoreKey = keys[1];
    const countKey = keys[2];

    if (useBinning) {
      // Find min and max values for binning
      const minX = Math.min(...selectedChartData.map((item) => item[nameKey]));
      const maxX = Math.max(...selectedChartData.map((item) => item[nameKey]));

      // Calculate bin width dynamically
      const binWidth = parseFloat(((maxX - minX) / binSize).toFixed(2));

      // Step 1: Create bins dynamically with "lower-upper" labels
      xValues = Array.from({ length: binSize }, (_, i) => {
        const lower = parseFloat((minX + i * binWidth).toFixed(2));
        const upper = parseFloat((lower + binWidth).toFixed(2));
        return `${lower}-${upper}`;
      });
    } else {
      // No binning: Use raw unique x-values
      xValues = [...new Set(selectedChartData.map((item) => item[nameKey]))].sort(
        (a, b) => a - b,
      );
    }

    // Step 2: Extract unique categories dynamically
    categories = [...new Set(selectedChartData.map((item) => item[scoreKey]))];

    // Assign colors to categories
    colorsMap = categories.includes(null) ? { [NULL_VALUE_TITLE]: '100,120,230' } : {};
    GetGlobalExpData()
      ?.scoreDefn.filter((score) => categories.includes(score.scoreName))
      .forEach((score) => {
        colorsMap[score.scoreName] = score.color;
      });

    // Initialize series data for each category
    seriesData = categories.map((category) => ({
      name: category ? category : NULL_VALUE_TITLE,
      type: 'bar',
      stack: 'total',
      data: Array(xValues.length).fill(0), // Initialize with zeros
      itemStyle: {
        color: `rgb(${colorsMap[category ? category : NULL_VALUE_TITLE]})`,
      },
    }));

    // Step 3: Assign data to the correct bins or exact values
    selectedChartData.forEach((item) => {
      const xValue =
        useBinning && item[nameKey]
          ? parseFloat(item[nameKey].toFixed(2))
          : item[nameKey];
      const seriesIndex = categories.indexOf(item[scoreKey]);

      let xIndex;
      if (useBinning) {
        // Determine the bin index where this value falls
        const minX = parseFloat(xValues[0].split('-')[0]); // Extract minX from first bin
        const binWidth = parseFloat((xValues[0].split('-')[1] - minX).toFixed(2));

        xIndex = Math.floor((xValue - minX) / binWidth);
        xIndex = xIndex >= binSize ? binSize - 1 : xIndex; // Ensure last value falls in last bin
      } else {
        // Find exact x-axis index
        xIndex = xValues.indexOf(xValue);
      }

      seriesData[seriesIndex].data[xIndex] += item[countKey];
    });

    return { seriesData, categories, xValues, colorsMap };
  }, [selectedChart, GetGlobalExpData, binSize, useBinning]);
};
